import cx from 'classnames'
import PropTypes from 'prop-types'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import useHotkeys from '@reecelucas/react-use-hotkeys'

import { focusMainContent } from 'shared/utils/focus'

import { useNoty } from 'shared/hooks'

import {
  CloseButtonUI,
  IconUI,
  NotyButtonUI,
  NotyContentUI,
  NotyElementUI,
} from './NotyElement.css'

import {
  ItemShape,
  STATUS_ERROR,
  STATUS_MESSAGE,
  STATUS_SUCCESS,
  UNDO_HOTKEY,
} from '../../Noty.constants'

// Off until we settle how toasts with buttons should behave; set to true to
// keep them open until dismissed (WCAG 2.2.1).
const KEEP_TOASTS_WITH_BUTTONS_OPEN = false

/**
 * @typedef {import('../../Noty.constants').TNotyItem} TNotyItem
 * @param {TNotyItem & {hasUndoHotkey?: boolean}} props
 */
const NotyElement = props => {
  const {
    content = '',
    id = 1,
    status = '',
    opts = {},
    hasUndoHotkey = false,
  } = props

  const {
    timeout = 10000,
    expires = false,
    icon = null,
    primaryButtonProps = null,
    dangerButtonProps = null,
  } = opts

  const hasButtons = Boolean(primaryButtonProps || dangerButtonProps)
  const staysOpen = KEEP_TOASTS_WITH_BUTTONS_OPEN && hasButtons && !expires
  const autoCloses = Boolean(timeout) && !staysOpen

  const elementRef = useRef()
  const returnFocusRef = useRef(null)
  const remainingRef = useRef(timeout)

  const { hideNoty } = useNoty()

  const [visible, setVisible] = useState(true)
  const [deleted, setDeleted] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [hasFocus, setHasFocus] = useState(false)

  const isPaused = !expires && (isHovered || hasFocus)

  const restoreFocus = useCallback(() => {
    const target = returnFocusRef.current
    target?.focus()
    if (document.activeElement !== target) focusMainContent()
  }, [])

  const close = useCallback(() => {
    if (elementRef.current?.contains(document.activeElement)) restoreFocus()
    setVisible(false)
  }, [restoreFocus])

  // The reducer can remove a toast without close, e.g. a duplicate toast.
  useLayoutEffect(() => {
    const element = elementRef.current
    return () => {
      if (element?.contains(document.activeElement)) restoreFocus()
    }
  }, [restoreFocus])

  useEffect(() => {
    if (!visible || !autoCloses || isPaused) return
    const startedAt = Date.now()
    const timer = setTimeout(close, remainingRef.current)

    return () => {
      clearTimeout(timer)
      remainingRef.current -= Date.now() - startedAt
    }
  }, [autoCloses, close, isPaused, visible])

  const handleTransitionEnd = e => {
    if (e.propertyName === 'transform') {
      setDeleted(true)
      hideNoty(id)
    }
  }

  const handleFocus = e => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      returnFocusRef.current = e.relatedTarget
    }
    setHasFocus(true)
  }

  const handleBlur = e => {
    if (!e.currentTarget.contains(e.relatedTarget)) setHasFocus(false)
  }

  const handleKeyDown = e => {
    if (e.key !== 'Escape') return
    e.stopPropagation()
    close()
  }

  useHotkeys(
    UNDO_HOTKEY,
    () => {
      if (!dangerButtonProps?.onClick) return
      dangerButtonProps.onClick()
      close()
    },
    {
      enabled: visible && hasUndoHotkey,
    }
  )

  const getTheme = status => {
    switch (status) {
      case STATUS_ERROR:
        return 'red'
      case STATUS_MESSAGE:
        return 'blue'
      case STATUS_SUCCESS:
        return 'green'
      default:
        return 'grey'
    }
  }

  const notyClassnames = cx(visible && 'is-visible', `is-${status}`)

  if (deleted) return null

  return (
    <NotyElementUI
      ref={elementRef}
      className={notyClassnames}
      onClick={close}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onTransitionEnd={handleTransitionEnd}
      data-testid="NotyElement"
    >
      {Boolean(icon) && <IconUI data-testid="NotyIcon" size={24} icon={icon} />}
      <NotyContentUI>{content}</NotyContentUI>
      {Boolean(primaryButtonProps) && (
        <NotyButtonUI color={getTheme(status)} {...primaryButtonProps} />
      )}
      {Boolean(dangerButtonProps) && (
        <NotyButtonUI
          color="grey"
          {...(hasUndoHotkey && {
            shortcut: UNDO_HOTKEY.toUpperCase(),
            'aria-keyshortcuts': UNDO_HOTKEY,
          })}
          {...dangerButtonProps}
        />
      )}
      {/* No onClick: a click anywhere in the toast closes it. */}
      <CloseButtonUI title="Close notification" size="sm" seamless />
    </NotyElementUI>
  )
}

NotyElement.propTypes = {
  ...ItemShape,
  hasUndoHotkey: PropTypes.bool,
}

export default NotyElement
